import { ArrowLeftOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { Dropdown, Flex, Input, type MenuProps, Skeleton, Timeline, Typography, message } from "antd";
import dayjs from "dayjs";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getNotesAndBadge } from "../../domain/notes.domain";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useFuseSearch } from "../../hooks/useFuseSearch";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { deleteClient } from "../../service/client.service";
import {
  setCreateClientState,
  setLoadingNotesState,
  setNoteGenerating,
  setNotesViewVisible,
  setSelectedClient,
  setShareClientModalClient,
  setShareClientModalVisible,
  setShowDemoSignupModal,
} from "../../slices/appStateSlice";
import { removeClient, setClientForm, setIsEditing } from "../../slices/clientSlice";
import {
  mergeClientNote,
  setClientNotes,
  setNotesDetails,
  setRecordingId,
  setTotalNotes,
} from "../../slices/notesSlice";
import type { TNotes } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import { eventEmitter } from "../../utils/eventEmitter";
import styles from "./home.module.scss";

const componentName = "home";
const cls = (suffix: string) => styles[`${componentName}__container-sidebar-timeline${suffix}`];

const SEARCH_KEYS = ["title", "note_type_info.name"];

const ClientTimeline = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { rootPath } = useRootPath();
  const { referIndividualBy } = useSpecialityReference();

  const { selectedClient, loadingNotes, isDemoPath, isMobilePath } = useAppSelector((state) => state.appState);
  const clientNotes = useAppSelector((state) => state.notes.clientNotes);
  const notesDetails = useAppSelector((state) => state.notes.notesDetails);

  const [searchTerm, setSearchTerm] = React.useState("");

  const { search } = useFuseSearch<TNotes>({
    data: clientNotes,
    keys: SEARCH_KEYS,
  });
  const filteredNotes = searchTerm.trim() ? search(searchTerm) : clientNotes;

  // Group notes by calendar day, newest first (Fuse sorts by relevance, so re-sort chronologically)
  const sortedNotes = [...filteredNotes].sort(
    (a, b) => dayjs(b.uploaded_at).valueOf() - dayjs(a.uploaded_at).valueOf(),
  );
  const groups = new Map<string, TNotes[]>();
  for (const note of sortedNotes) {
    const day = dayjs(note.uploaded_at).format("YYYY-MM-DD");
    const group = groups.get(day);
    if (group) {
      group.push(note);
    } else {
      groups.set(day, [note]);
    }
  }
  const groupedNotes = Array.from(groups.values());

  if (!selectedClient) return null;

  const firstName = (selectedClient.name || "").split(" ")[0] || selectedClient.name;
  const metaParts: string[] = [];
  if (selectedClient.date_of_birth) {
    metaParts.push(`DOB: ${dayjs(selectedClient.date_of_birth).format("MM/DD/YYYY")}`);
  }
  if (selectedClient.mrn) {
    metaParts.push(`MRN: ${selectedClient.mrn}`);
  }

  const backToAllClients = () => {
    dispatch(setLoadingNotesState(true));
    dispatch(setTotalNotes(0));
    dispatch(setClientNotes([]));
    dispatch(setSelectedClient(null));
    dispatch(setNotesViewVisible(false));
    navigate(rootPath);
    eventEmitter.emit("refreshNotes");
  };

  const openNote = (item: TNotes) => {
    if (item.error_status !== 0 || item.status !== 1) {
      return;
    }
    dispatch(mergeClientNote({ ...item, is_read: true }));
    trackEvent(EventType.SELECT_NOTE, { recordingId: item.id });
    dispatch(setNoteGenerating(null));
    dispatch(setNotesDetails(item));
    dispatch(setLoadingNotesState(true));
    dispatch(setNotesViewVisible(true));
    dispatch(setRecordingId(item.id));

    // Ensure the note opens in the right-side pane: the notes container only
    // renders ClientNotes on the client route.
    if (selectedClient.uuid && !location.pathname.includes(`/clients/${selectedClient.uuid}`)) {
      navigate(`${rootPath}/clients/${selectedClient.uuid}`);
    }
  };

  const isOwnedClient = selectedClient.is_owned !== false;
  const canEditClient = selectedClient.can_edit !== false;

  const handleMenu = (e: any) => {
    e.domEvent?.stopPropagation();
    if (e.key === "edit") {
      dispatch(setCreateClientState(true));
      dispatch(setClientForm(selectedClient));
      dispatch(setIsEditing(true));
    }
    if (e.key === "share") {
      dispatch(setShareClientModalClient(selectedClient));
      dispatch(setShareClientModalVisible(true));
    }
    if (e.key === "delete") {
      if (isDemoPath) {
        dispatch(setShowDemoSignupModal(true));
        return;
      }
      Swal.fire({
        title: `Would you like to delete this ${referIndividualBy.toLowerCase()}?`,
        text: `The ${referIndividualBy.toLowerCase()}'s notes will still be available unless you delete them.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, keep it",
      }).then((result) => {
        if (result.isConfirmed && selectedClient.id) {
          deleteClient(selectedClient.id)
            .then(() => {
              dispatch(removeClient(selectedClient.id as number));
              backToAllClients();
              message.success(`${referIndividualBy} deleted successfully`);
            })
            .catch((error) => {
              console.error(error);
              message.error(`Failed to delete ${referIndividualBy.toLowerCase()}.`);
            });
        }
      });
    }
  };

  const menuItems: MenuProps["items"] = [
    ...(canEditClient && !isMobilePath ? [{ key: "edit", label: <span>Edit {referIndividualBy}</span> }] : []),
    ...(isOwnedClient && !isMobilePath ? [{ key: "share", label: <span>Share {referIndividualBy}</span> }] : []),
    ...(isOwnedClient ? [{ key: "delete", label: <span>Delete {referIndividualBy}</span> }] : []),
  ];

  return (
    <Flex vertical className={cls("")}>
      <div className={cls("-back")} onClick={backToAllClients} role="button" tabIndex={0}>
        <ArrowLeftOutlined /> Back to All {referIndividualBy}s
      </div>

      <Flex align="center" justify="space-between" gap={8} className={cls("-client-card")}>
        <Flex vertical style={{ flex: 1, minWidth: 0 }}>
          <Typography className={cls("-client-name")}>{selectedClient.name}</Typography>
          {metaParts.length > 0 && <Typography className={cls("-client-meta")}>{metaParts.join(" • ")}</Typography>}
        </Flex>
        {menuItems.length > 0 && (
          <Dropdown menu={{ items: menuItems, onClick: handleMenu }} placement="bottomRight" trigger={["click"]}>
            <MoreOutlined className={cls("-client-menu")} />
          </Dropdown>
        )}
      </Flex>

      <Input
        value={searchTerm}
        prefix={<SearchOutlined />}
        placeholder={`Search ${firstName}'s Sessions...`}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
        style={{ padding: "5px 10px" }}
      />

      <div className={cls("-scroll")}>
        {loadingNotes && clientNotes.length === 0 ? (
          <Skeleton active />
        ) : groupedNotes.length > 0 ? (
          <Timeline
            className={cls("-list")}
            items={groupedNotes.map((group) => ({
              key: group[0].id,
              color: "gray",
              children: (
                <>
                  <div className={cls("-group-header")}>
                    {dayjs(group[0].uploaded_at).format("MMM D, YYYY, h:mm A")}
                  </div>
                  {group.map((item) => {
                    const noteTypeLabel = item.note_type_info?.name || getNotesAndBadge(item.note_type).name;
                    const isOpen = notesDetails?.id === item.id;
                    return (
                      <div
                        key={item.id}
                        className={`${cls("-card")} ${isOpen ? cls("-card--active") : ""}`}
                        onClick={() => openNote(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && openNote(item)}
                      >
                        <div className={cls("-card-title")}>{item.title}</div>
                        {noteTypeLabel && (
                          <div className={cls("-card-type")}>
                            Note Type: <span>{noteTypeLabel}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              ),
            }))}
          />
        ) : (
          <Typography className={cls("-empty")}>
            {searchTerm ? "No notes match your search." : "No notes yet."}
          </Typography>
        )}
        {groupedNotes.length > 0 && <div className={cls("-end")}>End of sessions</div>}
      </div>
    </Flex>
  );
};

export default ClientTimeline;
